import { useEffect, useState } from "react";
import { Eye, Users, CalendarDays, Loader2, TrendingUp, ShieldCheck, ShieldAlert, LogIn, Globe } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { formatApiErrorDetail } from "../../lib/api";

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-5">
    <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${accent}`}>
      <Icon size={20} />
    </span>
    <p className="mt-4 font-display text-3xl font-black text-navy">{value}</p>
    <p className="text-sm text-muted-foreground">{label}</p>
  </div>
);

export const AnalyticsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    api.get("/admin/analytics")
      .then(({ data }) => setData(data))
      .catch((err) => formatApiErrorDetail(err.response?.data?.detail))
      .finally(() => setLoading(false));
    api.get("/admin/login-audit").then(({ data }) => setAudit(data)).catch(() => {});
  }, []);

  const fmtTs = (ts) => { try { return new Date(ts).toLocaleString(); } catch { return ts; } };

  const flag = (code) => {
    if (!code || code.length !== 2) return "🌍";
    try { return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 127397 + c.charCodeAt(0))); } catch { return "🌍"; }
  };
  const countryName = (code) => {
    if (!code || code === "Unknown") return "Unknown";
    try { return new Intl.DisplayNames(["en"], { type: "region" }).of(code.toUpperCase()) || code; } catch { return code; }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>;
  if (!data) return <p className="rounded-xl border border-gray-200 bg-white p-10 text-center text-sm text-muted-foreground">Could not load analytics.</p>;

  const chartData = (data.series || []).map((s) => ({ ...s, label: s.day?.slice(5) }));

  return (
    <div data-testid="analytics-panel">
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard icon={Eye} label="Total Page Views" value={data.total_views} accent="bg-navy text-white" />
        <StatCard icon={Users} label="Unique Visitors" value={data.unique_visitors} accent="bg-gold text-navy" />
        <StatCard icon={CalendarDays} label="Views Today" value={data.today_views} accent="bg-brandred text-white" />
      </div>

      <div className="mt-6 rounded-xl border border-gray-200 bg-white p-5">
        <div className="mb-4 flex items-center gap-2">
          <TrendingUp size={18} className="text-navy" />
          <p className="font-display font-bold text-navy">Visitors (last 30 days)</p>
        </div>
        {chartData.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">No visits recorded yet.</p>
        ) : (
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 12 }} />
                <Bar dataKey="views" name="Page views" fill="#0A2C66" radius={[4, 4, 0, 0]} />
                <Bar dataKey="visitors" name="Unique visitors" fill="#D9A441" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {data.top_pages?.length > 0 && (
        <div className="mt-6 rounded-xl border border-gray-200 bg-white p-5">
          <p className="mb-3 font-display font-bold text-navy">Top Pages</p>
          <ul className="divide-y divide-gray-100">
            {data.top_pages.map((p) => (
              <li key={p.path} className="flex items-center justify-between py-2.5 text-sm">
                <span className="font-medium text-navy">{p.path}</span>
                <span className="text-muted-foreground">{p.views} views</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.by_country?.length > 0 && (
        <div className="mt-6 rounded-xl border border-gray-200 bg-white p-5" data-testid="country-panel">
          <div className="mb-3 flex items-center gap-2">
            <Globe size={18} className="text-navy" />
            <p className="font-display font-bold text-navy">Visitors by Country</p>
          </div>
          <ul className="space-y-2.5">
            {(() => { const max = Math.max(...data.by_country.map((c) => c.views)); return data.by_country.map((c) => (
              <li key={c.country} data-testid={`country-row-${c.country}`} className="flex items-center gap-3">
                <span className="w-6 text-lg leading-none">{flag(c.country)}</span>
                <span className="w-32 shrink-0 truncate text-sm font-semibold text-navy">{countryName(c.country)}</span>
                <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-navy-50">
                  <div className="h-full rounded-full bg-navy" style={{ width: `${Math.max(4, (c.views / max) * 100)}%` }} />
                </div>
                <span className="w-24 shrink-0 text-right text-xs text-muted-foreground">{c.visitors} visitor{c.visitors === 1 ? "" : "s"}</span>
              </li>
            )); })()}
          </ul>
        </div>
      )}


      <div className="mt-6 rounded-xl border border-gray-200 bg-white p-5" data-testid="login-audit-panel">
        <div className="mb-3 flex items-center gap-2">
          <LogIn size={18} className="text-navy" />
          <p className="font-display font-bold text-navy">Recent Admin Sign-in Activity</p>
        </div>
        {audit.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No sign-in activity recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                <tr><th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Email</th><th className="py-2 pr-4">IP address</th><th className="py-2">When</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {audit.map((a, i) => (
                  <tr key={i} data-testid={`audit-row-${i}`}>
                    <td className="py-2.5 pr-4">
                      {a.success ? (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-green-600"><ShieldCheck size={14} /> Success</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-brandred"><ShieldAlert size={14} /> {a.locked ? "Blocked" : "Failed"}</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 font-medium text-navy">{a.email}</td>
                    <td className="py-2.5 pr-4 text-muted-foreground">{a.ip}</td>
                    <td className="py-2.5 text-muted-foreground">{fmtTs(a.ts)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
